/** One spoken walk line: a material plus the two quantities that line needs. */

import { matchSpokenLineToCatalog, type CatalogCandidate, type CatalogMatch } from "./spokenCatalogMatch";
import { bareSpokenToken, parseSpokenQty } from "./spokenNumberWords";
import { parsePackedPlain, type SpokenWalkDraft } from "./spokenWalkPlain";
import { looksLikeWasteMonologue, parseWasteMonologue } from "./spokenWasteMonologue";

export type { SpokenWalkDraft } from "./spokenWalkPlain";

export type SpokenWalkKind = "packed" | "waste";

const SKIP = new Set(["item", "items", "of", "and"]);

const PHRASES = {
  packed: [["packed"]],
  bay: [
    ["in", "the", "bays"],
    ["in", "the", "bay"],
    ["bays"],
    ["bay"],
  ],
  seen: [
    ["i", "see"],
    ["seen"],
    ["see"],
  ],
  use: [
    ["tell", "them", "to", "use"],
    ["tell", "them", "use"],
    ["to", "use"],
    ["use"],
  ],
} as const;

function phraseAt(tokens: string[], index: number, phrases: readonly (readonly string[])[]): number {
  for (const phrase of phrases) {
    const hit = phrase.every((word, offset) => bareSpokenToken(tokens[index + offset] ?? "") === word);
    if (hit) return phrase.length;
  }
  return 0;
}

function qtyAt(tokens: string[], index: number): { value: number; next: number } | null {
  const parsed = parseSpokenQty(tokens.slice(index));
  if (!parsed) return null;
  return { value: parsed.value, next: index + parsed.consumed };
}

function parseChunk(kind: SpokenWalkKind, raw: string): SpokenWalkDraft | null {
  const tokens = raw.trim().split(/\s+/).filter((token) => token.length > 0);
  if (tokens.length === 0) return null;
  const primaryLabel = kind === "packed" ? "packed" : "seen";
  const secondaryLabel = kind === "packed" ? "bay" : "use";
  let primary: number | null = null;
  let secondary: number | null = null;
  const name: string[] = [];
  let i = 0;
  while (i < tokens.length) {
    const primaryLen = phraseAt(tokens, i, PHRASES[primaryLabel]);
    const secondaryLen = primaryLen ? 0 : phraseAt(tokens, i, PHRASES[secondaryLabel]);
    const len = primaryLen || secondaryLen;
    if (!len) {
      name.push(tokens[i] ?? "");
      i += 1;
      continue;
    }
    const preceding = name.length > 0 ? parseSpokenQty([name[name.length - 1] ?? ""]) : null;
    if (preceding?.consumed === 1) {
      name.pop();
      if (primaryLen) primary = preceding.value;
      else secondary = preceding.value;
      i += len;
      continue;
    }
    const following = qtyAt(tokens, i + len);
    if (!following) {
      name.push(...tokens.slice(i, i + len));
      i += len;
      continue;
    }
    if (primaryLen) primary = following.value;
    else secondary = following.value;
    i = following.next;
  }
  const materialName = name
    .map((token) => token.trim())
    .filter((token) => token.length > 0 && !SKIP.has(bareSpokenToken(token)))
    .join(" ")
    .replace(/[.,;:]+$/g, "")
    .trim();
  if (primary == null || !materialName) return null;
  return { primaryQty: primary, secondaryQty: secondary, materialName, raw: raw.trim() };
}

function nameHasShippingChatter(name: string): boolean {
  return /\b(pallets?|bundles?|skids?|sheets?|pieces?|boards?|panels?)\b/i.test(name);
}

function pickPacked(rigid: SpokenWalkDraft | null, plain: SpokenWalkDraft | null): SpokenWalkDraft | null {
  if (plain && (!rigid || nameHasShippingChatter(rigid.materialName))) return plain;
  return rigid;
}

/** Every pile in the utterance. Packed stays one line; a waste walk can name several. */
export function parseSpokenWalkLines(kind: SpokenWalkKind, transcript: string): SpokenWalkDraft[] {
  if (kind === "waste" && looksLikeWasteMonologue(transcript)) {
    const lines = parseWasteMonologue(transcript);
    if (lines.length > 0) return lines;
  }
  const chunks = transcript
    .split(/\n+/)
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.length > 0);
  const out: SpokenWalkDraft[] = [];
  for (const chunk of chunks) {
    const rigid = parseChunk(kind, chunk);
    const picked = kind === "packed" ? pickPacked(rigid, parsePackedPlain(chunk)) : rigid;
    if (picked) out.push(picked);
  }
  return out;
}

/** First chunk that has a primary quantity and a material name. */
export function parseSpokenWalk(kind: SpokenWalkKind, transcript: string): SpokenWalkDraft | null {
  return parseSpokenWalkLines(kind, transcript)[0] ?? null;
}

export function matchSpokenWalkMaterial(
  draft: SpokenWalkDraft,
  catalog: readonly CatalogCandidate[],
): CatalogMatch {
  return matchSpokenLineToCatalog(
    { qty: draft.primaryQty, name: draft.materialName, raw: draft.raw },
    catalog,
  );
}
